import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { env } from '@/lib/env';

/**
 * Sits inside RequireAuth and routes admin-tier users through the security
 * gauntlet before letting them into the app:
 *
 *  1. **MFA enforcement** — TENANT-SCOPED. When the user's tenant has
 *     `requireMfa` on, every unenrolled user is routed to /mfa-setup. The
 *     policy is opt-in per org (tenant admins set it in the platform app's
 *     Security settings); MFA is otherwise a per-user choice.
 *
 *  2. **Re-consent** — TENANT_ADMIN whose `consentRefreshRequired` is non-empty
 *     are routed to /reconsent. The flag is server-computed on /auth/me; the
 *     runtime omits it for PLATFORM_ADMIN by design (they don't operate on
 *     the tenant's behalf). Bypassable in dev via VITE_BYPASS_CONSENT_GATE.
 *
 * MFA is no longer a global build flag — a tenant that turns it on applies it to
 * ALL its users (including floor staff); one that leaves it off keeps MFA
 * optional for everyone. The per-org choice lives on the tenant.
 */
const ALLOWED_PATHS = new Set([
  '/mfa-setup',
  '/reconsent',
  '/login',
  '/login/forgot',
  '/login/reset',
  '/login/invite',
  '/login/mfa',
  '/suspended',
  '/settings/profile', // self-service MFA enrollment lives here too
]);

export function ComplianceGate() {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  const needsMfa = !!me && !me.mfaEnrolled && !!me.requireMfa;
  const needsReconsent =
    !!me &&
    env.requireConsentGate &&
    Array.isArray(me.consentRefreshRequired) &&
    me.consentRefreshRequired.length > 0;

  useEffect(() => {
    if (!me || ALLOWED_PATHS.has(location.pathname)) return;

    if (needsMfa && location.pathname !== '/mfa-setup') {
      navigate('/mfa-setup', { replace: true });
      return;
    }
    if (needsReconsent && location.pathname !== '/reconsent') {
      navigate('/reconsent', { replace: true });
      return;
    }
  }, [me, needsMfa, needsReconsent, location.pathname, navigate]);

  return <Outlet />;
}
